import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth';
import { NavBar } from '../../components/NavBar';
import { SharedFooter } from '../../components/SharedFooter';
import { ReferencePackCard } from '../reference-packs/ReferencePackCard';
import type { FollowProfile } from '@modelcast/types';
import { authHeaders, jsonAuthHeaders } from '../../shared/authHeaders';

interface Room {
  id: string;
  role?: 'hosting' | 'modeling' | 'attending';
  code: string;
  name: string;
  description: string | null;
  scheduledAt: string | null;
  endsAt: string | null;
  startedAt: string | null;
  closedAt: string | null;
  cancelledAt?: string | null;
  imageUrl: string | null;
  isPublic: boolean;
  isPublished: boolean;
  isTutored: boolean;
  seatsTotal: number | null;
  seatsConfirmed: number;
  settings: {
    maxParticipants?: number;
  } | null;
  modelName: string | null;
  modelOption?: 'none' | 'self' | 'invite' | 'ad';
  modelInviteStatus?: 'pending' | 'confirmed' | 'declined' | null;
  modelInviteName?: string | null;
  modelInviteEmail?: string | null;
}

interface PurchasedReferencePack {
  id: string;
  title: string;
  description: string | null;
  priceCents: number | null;
  currency: string | null;
  coverImageUrl: string | null;
  license: string | null;
  captureIds: string[];
  recordingIds: string[];
  room: {
    code: string;
    name: string;
  };
  purchase: {
    accessExpiresOn: string | null;
  };
}

interface ModelInvitation {
  inviteId: string;
  inviteToken: string | null;
  rsvpStatus: 'pending' | 'confirmed' | 'declined';
  createdAt: string;
  hostName: string | null;
  hostEmail: string | null;
  room: {
    id: string;
    code: string;
    name: string;
    description: string | null;
    scheduledAt: string | null;
    endsAt: string | null;
    timezone: string | null;
    duration: number;
    imageUrl: string | null;
    pricingType: 'free' | 'fixed' | 'donation';
    ticketPrice: number | null;
    suggestedMinimum: number | null;
    revenueSplitMode?: 'manual' | 'even';
    modelSplitPercent: number;
    modelGuarantee: number | null;
    modelExpectations: string | null;
    maxAttendees: number | null;
  };
}

function shouldLogDashboardInvitations() {
  return import.meta.env.DEV || window.localStorage.getItem('debugDashboardInvitations') === 'true';
}

function logDashboardInvitations(message: string, details?: unknown) {
  if (!shouldLogDashboardInvitations()) return;
  console.info(`[Dashboard invitations] ${message}`, details ?? '');
}

function summarizeInvitation(invitation: ModelInvitation) {
  return {
    inviteId: invitation.inviteId,
    hasInviteToken: Boolean(invitation.inviteToken),
    rsvpStatus: invitation.rsvpStatus,
    hostName: invitation.hostName,
    room: {
      id: invitation.room.id,
      code: invitation.room.code,
      name: invitation.room.name,
      scheduledAt: invitation.room.scheduledAt,
      pricingType: invitation.room.pricingType,
    },
  };
}

function summarizeRoomInviteState(room: Room) {
  return {
    id: room.id,
    code: room.code,
    name: room.name,
    role: room.role,
    isPublished: room.isPublished,
    scheduledAt: room.scheduledAt,
    endsAt: room.endsAt,
    startedAt: room.startedAt,
    closedAt: room.closedAt,
    modelOption: room.modelOption,
    modelInviteStatus: room.modelInviteStatus,
    modelInviteName: room.modelInviteName,
    modelInviteEmail: room.modelInviteEmail,
    modelName: room.modelName,
  };
}

function getRoomDashboardFilterDecision(room: Room, now: Date, pendingModelInviteRoomIds: Set<string>) {
  const scheduledAt = room.scheduledAt ? new Date(room.scheduledAt) : null;
  const endsAt = room.endsAt ? new Date(room.endsAt) : null;
  const isCancelled = Boolean(room.cancelledAt);
  const isPendingHostModelInvite = room.role === 'hosting'
    && room.modelOption === 'invite'
    && room.modelInviteStatus === 'pending'
    && !room.closedAt
    && !isCancelled;
  const isDraft = room.role !== 'modeling'
    && room.role !== 'attending'
    && !room.isPublished
    && !room.closedAt
    && !isCancelled;
  const isModeling = room.role === 'modeling';
  const isUpcoming = !pendingModelInviteRoomIds.has(room.id)
    && (room.isPublished || isModeling)
    && !room.closedAt
    && !isCancelled
    && (
      Boolean(room.startedAt)
      || (scheduledAt ? scheduledAt > now : true)
    );
  const isUpcomingModeling = isUpcoming && isModeling;
  const isUpcomingSession = isUpcoming && !isModeling;
  const isPast = isCancelled
    || ((room.isPublished || isModeling)
      && (
        Boolean(room.closedAt)
        || Boolean(endsAt && endsAt < now && !room.startedAt)
      ));

  const includedIn = [
    isPendingHostModelInvite ? 'invitations:host-pending-model-invite' : null,
    isDraft ? 'sessions:draft' : null,
    isUpcomingModeling ? 'sessions:upcoming-modeling' : null,
    isUpcomingSession ? 'sessions:upcoming-session' : null,
    isPast ? 'past' : null,
  ].filter(Boolean);

  const exclusionReasons = [];
  if (!room.isPublished && !isModeling) exclusionReasons.push('not published');
  if (room.closedAt) exclusionReasons.push('closed');
  if (pendingModelInviteRoomIds.has(room.id)) exclusionReasons.push('withheld from upcoming because host model invite is pending');
  if (room.role === 'modeling' && !isUpcomingModeling) exclusionReasons.push('modeling room is not upcoming');
  if (scheduledAt && scheduledAt <= now && !room.startedAt && !room.closedAt) exclusionReasons.push('scheduled time is not in the future and session is not live');
  if (!scheduledAt && isModeling) exclusionReasons.push('modeling room has no scheduled date');

  return {
    ...summarizeRoomInviteState(room),
    includedIn,
    exclusionReasons: includedIn.length === 0 ? exclusionReasons : [],
    scheduledTimestamp: scheduledAt?.getTime() ?? null,
    endsTimestamp: endsAt?.getTime() ?? null,
    nowTimestamp: now.getTime(),
  };
}

export function HostDashboardPage() {
  const navigate = useNavigate();
  const { getToken } = useAuth();
  const [rooms, setRooms] = useState<Room[]>([]);
  const [modelInvitations, setModelInvitations] = useState<ModelInvitation[]>([]);
  const [followers, setFollowers] = useState<FollowProfile[]>([]);
  const [purchasedPacks, setPurchasedPacks] = useState<PurchasedReferencePack[]>([]);
  const [loading, setLoading] = useState(true);
  const [respondingInviteId, setRespondingInviteId] = useState<string | null>(null);

  useEffect(() => {
    async function fetchData() {
      const token = await getToken();

      // Fetch rooms and pending model invitations (required)
      try {
        logDashboardInvitations('fetching dashboard data', { hasToken: Boolean(token) });
        const headers = authHeaders(token);

        const [roomsRes, invitationsRes, followersRes, packsRes] = await Promise.all([
          fetch('/api/rooms/my-events', {
            headers,
          }),
          fetch('/api/rooms/my-model-invitations', {
            headers,
          }),
          fetch('/api/follows/followers', {
            headers,
          }),
          fetch('/api/products/reference-packs/purchased', {
            headers,
          }),
        ]);

        if (roomsRes.ok) {
          const data = await roomsRes.json();
          logDashboardInvitations('my-events response', {
            status: roomsRes.status,
            roomCount: data.rooms?.length ?? 0,
            rooms: (data.rooms || []).map(summarizeRoomInviteState),
            roomsWithModelInviteState: (data.rooms || [])
              .filter((room: Room) => room.modelOption === 'invite' || room.modelInviteStatus || room.role === 'modeling')
              .map(summarizeRoomInviteState),
          });
          setRooms(data.rooms || []);
        } else {
          const body = await roomsRes.text().catch(() => '');
          console.warn('[Dashboard invitations] my-events request failed', {
            status: roomsRes.status,
            body,
          });
        }

        if (invitationsRes.ok) {
          const data = await invitationsRes.json();
          logDashboardInvitations('my-model-invitations response', {
            status: invitationsRes.status,
            invitationCount: data.invitations?.length ?? 0,
            invitations: (data.invitations || []).map(summarizeInvitation),
          });
          setModelInvitations(data.invitations || []);
        } else {
          const body = await invitationsRes.text().catch(() => '');
          console.warn('[Dashboard invitations] my-model-invitations request failed', {
            status: invitationsRes.status,
            body,
          });
        }

        if (followersRes.ok) {
          const data = await followersRes.json();
          setFollowers(data.followers || []);
        }

        if (packsRes.ok) {
          const data = await packsRes.json();
          setPurchasedPacks(data.products || []);
        }

      } catch (error) {
        console.error('Failed to fetch dashboard data:', error);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [getToken]);

  const refreshModelInvitations = async () => {
    try {
      const token = await getToken();
      const response = await fetch('/api/rooms/my-model-invitations', {
        headers: authHeaders(token),
      });
      if (response.ok) {
        const data = await response.json();
        logDashboardInvitations('refreshed my-model-invitations', {
          status: response.status,
          invitationCount: data.invitations?.length ?? 0,
          invitations: (data.invitations || []).map(summarizeInvitation),
        });
        setModelInvitations(data.invitations || []);
        window.dispatchEvent(new Event('model-invitations:updated'));
      } else {
        const body = await response.text().catch(() => '');
        console.warn('[Dashboard invitations] refresh my-model-invitations failed', {
          status: response.status,
          body,
        });
      }
    } catch (error) {
      console.error('Failed to refresh model invitations:', error);
    }
  };

  const refreshRooms = async () => {
    try {
      const token = await getToken();
      const response = await fetch('/api/rooms/my-events', {
        headers: authHeaders(token),
      });
      if (response.ok) {
        const data = await response.json();
        logDashboardInvitations('refreshed my-events', {
          status: response.status,
          roomCount: data.rooms?.length ?? 0,
          rooms: (data.rooms || []).map(summarizeRoomInviteState),
          roomsWithModelInviteState: (data.rooms || [])
            .filter((room: Room) => room.modelOption === 'invite' || room.modelInviteStatus || room.role === 'modeling')
            .map(summarizeRoomInviteState),
        });
        setRooms(data.rooms || []);
      } else {
        const body = await response.text().catch(() => '');
        console.warn('[Dashboard invitations] refresh my-events failed', {
          status: response.status,
          body,
        });
      }
    } catch (error) {
      console.error('Failed to refresh rooms:', error);
    }
  };

  const respondToInvitation = async (invitation: ModelInvitation, rsvpStatus: 'confirmed' | 'declined') => {
    setRespondingInviteId(invitation.inviteId);
    try {
      const token = await getToken();
      logDashboardInvitations('responding to model invitation', {
        nextRsvpStatus: rsvpStatus,
        invitation: summarizeInvitation(invitation),
      });
      const response = await fetch(`/api/rooms/${invitation.room.code}/invites/${invitation.inviteId}`, {
        method: 'PATCH',
        headers: jsonAuthHeaders(token),
        body: JSON.stringify({ rsvpStatus }),
      });

      const responseBody = await response.json().catch(() => ({}));
      logDashboardInvitations('model invitation response received', {
        status: response.status,
        ok: response.ok,
        body: responseBody,
      });

      if (!response.ok) {
        throw new Error(responseBody.error || 'Failed to respond to invitation');
      }

      await refreshModelInvitations();
      await refreshRooms();
    } catch (error) {
      console.error('Failed to respond to model invitation:', error);
      alert(error instanceof Error ? error.message : 'Failed to respond to invitation');
    } finally {
      setRespondingInviteId(null);
    }
  };


  const now = new Date();

  // Separate rooms by publish status
  const draftRooms = rooms.filter((room) => {
    return room.role !== 'modeling' && room.role !== 'attending' && !room.isPublished && !room.closedAt && !room.cancelledAt;
  });

  const pendingModelInviteRooms = rooms.filter((room) => {
    return room.role === 'hosting' && room.modelOption === 'invite' && room.modelInviteStatus === 'pending' && !room.closedAt && !room.cancelledAt;
  });

  const upcomingRooms = rooms.filter((room) => {
    if (pendingModelInviteRooms.some(pendingRoom => pendingRoom.id === room.id)) return false;
    if (room.closedAt || room.cancelledAt) return false;
    if (!room.isPublished && room.role !== 'modeling') return false;
    if (room.startedAt && !room.closedAt) return true; // Live
    if (room.scheduledAt) {
      const scheduled = new Date(room.scheduledAt);
      return scheduled > now;
    }
    return true; // Published but no date = still upcoming
  });
  const upcomingModelingRooms = upcomingRooms.filter((room) => room.role === 'modeling');
  const upcomingSessionRooms = upcomingRooms.filter((room) => room.role !== 'modeling');

  const pastRooms = rooms.filter((room) => {
    if (room.cancelledAt) return true; // Cancelled events stay visible with a Cancelled badge.
    if (!room.isPublished && room.role !== 'modeling') return false; // Drafts aren't past unless the viewer modeled them.
    if (room.closedAt) return true;
    if (room.scheduledAt && room.endsAt) {
      const ends = new Date(room.endsAt);
      if (ends < now && !room.startedAt) return true;
    }
    return false;
  });

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
    });
  };

  const formatTime = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const isLive = (room: Room): boolean => Boolean(room.startedAt && !room.closedAt);
  const formatPastRole = (role: Room['role']) => {
    if (role === 'modeling') return 'Modeled';
    if (role === 'attending') return 'Attended';
    return 'Hosted';
  };
  const invitationCount = modelInvitations.length + pendingModelInviteRooms.length;
  const pendingModelInviteRoomIds = new Set(pendingModelInviteRooms.map((room) => room.id));

  useEffect(() => {
    logDashboardInvitations('derived dashboard state', {
      invitationCount,
      modelInvitationCount: modelInvitations.length,
      hostPendingModelInviteRoomCount: pendingModelInviteRooms.length,
      pendingModelInviteRooms: pendingModelInviteRooms.map(summarizeRoomInviteState),
      modelInvitations: modelInvitations.map(summarizeInvitation),
      roomCount: rooms.length,
      upcomingRoomCount: upcomingRooms.length,
      upcomingModelingRoomCount: upcomingModelingRooms.length,
      upcomingSessionRoomCount: upcomingSessionRooms.length,
      draftRoomCount: draftRooms.length,
      pastRoomCount: pastRooms.length,
      roomFilterDecisions: rooms.map((room) => getRoomDashboardFilterDecision(room, now, pendingModelInviteRoomIds)),
    });
  }, [invitationCount, modelInvitations, rooms]);

  return (
    <div className="min-h-screen bg-bg pt-[72px]">
      {/* Nav */}
      <NavBar
        variant="light"
        links={[
          { label: 'Drawing Events', href: '/events' },
          { label: 'Artists', href: '/artists' },
          { label: 'Models', href: '/models' },
        ]}
      />

      {/* Dashboard body */}
      {loading ? (
        <section className="py-16">
          <div className="page-container">
            <p className="text-text-dim">Loading your sessions...</p>
          </div>
        </section>
      ) : (
        <section className="py-12 border-b border-line">
          <div className="page-container space-y-14">
            <div className="page-header">
              <h1 className="page-heading">Dashboard</h1>
            </div>

            {/* Invitations */}
            {invitationCount > 0 && (
              <section>
                <div className="flex items-baseline justify-between pb-3.5 mb-6 border-b border-line">
                  <h2 className="section-title">Invitations</h2>
                  <span className="italic text-[12.5px] text-text-faint">{invitationCount}</span>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {modelInvitations.map((invitation) => (
                    <ModelInvitationCard
                      key={invitation.inviteId}
                      invitation={invitation}
                      responding={respondingInviteId === invitation.inviteId}
                      onAccept={() => respondToInvitation(invitation, 'confirmed')}
                      onDecline={() => respondToInvitation(invitation, 'declined')}
                    />
                  ))}
                  {pendingModelInviteRooms.map((room) => (
                    <HostPendingModelInviteCard key={room.id} room={room} />
                  ))}
                </div>
              </section>
            )}

            {/* Sessions (upcoming + drafts) */}
            <section>
              <div className="flex items-baseline justify-between pb-3.5 mb-6 border-b border-line gap-4">
                <h2 className="section-title">Sessions</h2>
                <Link
                  to="/create"
                  className="inline-flex items-center justify-center h-9 px-4 rounded bg-text text-bg text-[12.5px] font-medium hover:bg-black transition-colors shrink-0"
                >
                  Create a session
                </Link>
              </div>

              {upcomingModelingRooms.length > 0 && (
                <div className="mb-8">
                  <h3 className="text-[12px] uppercase text-text-dim mb-4">
                    Modeling
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                    {upcomingModelingRooms.map((room) => (
                      <EventCard
                        key={room.id}
                        room={room}
                        isLive={isLive(room)}
                        formatDate={formatDate}
                        formatTime={formatTime}
                      />
                    ))}
                  </div>
                </div>
              )}

              {upcomingSessionRooms.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                  {upcomingSessionRooms.map((room) => (
                    <EventCard
                      key={room.id}
                      room={room}
                      isLive={isLive(room)}
                      formatDate={formatDate}
                      formatTime={formatTime}
                    />
                  ))}
                </div>
              )}

              {draftRooms.length > 0 && (
                <div className={upcomingRooms.length > 0 ? 'mt-10' : ''}>
                  <h3 className="text-[12px] uppercase text-text-dim mb-4">
                    Drafts
                  </h3>
                  <div className="border-t border-line">
                    {draftRooms.map((room) => (
                      <DraftRow
                        key={room.id}
                        room={room}
                        formatDate={formatDate}
                        formatTime={formatTime}
                        onContinue={() => navigate('/create', { state: { roomCode: room.code, section: 'details' } })}
                      />
                    ))}
                  </div>
                </div>
              )}

              {upcomingRooms.length === 0 && draftRooms.length === 0 && (
                <div className="text-center py-10">
                  <p className="text-text-dim mb-5">No sessions yet.</p>
                  <Link to="/events" className="btn btn-primary">
                    Browse drawing events
                  </Link>
                </div>
              )}
            </section>

            {/* Purchases */}
            {purchasedPacks.length > 0 && (
              <section>
                <div className="flex items-baseline justify-between pb-3.5 mb-6 border-b border-line">
                  <h2 className="section-title">Purchases</h2>
                  <span className="italic text-[12.5px] text-text-faint">{purchasedPacks.length}</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                  {purchasedPacks.map((pack) => (
                    <ReferencePackCard
                      key={pack.id}
                      href={`/dashboard/packs/${pack.id}`}
                      priceLabelOverride="Open reference pack."
                      pack={{
                        title: pack.title,
                        description: pack.description,
                        priceCents: pack.priceCents,
                        currency: pack.currency,
                        coverImageUrl: pack.coverImageUrl,
                        photoCount: pack.captureIds.length,
                        videoCount: pack.recordingIds.length,
                        license: pack.license,
                        accessExpiresOn: pack.purchase.accessExpiresOn,
                      }}
                    />
                  ))}
                </div>
              </section>
            )}

            {/* Past sessions (collapsed) */}
            {pastRooms.length > 0 && (
              <details className="group">
                <summary className="flex items-center justify-between pb-3.5 mb-2 border-b border-line cursor-pointer list-none [&::-webkit-details-marker]:hidden">
                  <h2 className="section-title">
                    Past sessions
                    <span className="italic text-[12.5px] text-text-faint ml-2 font-normal">{pastRooms.length}</span>
                  </h2>
                  <span className="text-[12px] text-text-dim group-open:hidden">Show</span>
                  <span className="text-[12px] text-text-dim hidden group-open:inline">Hide</span>
                </summary>
                <table className="w-full border-collapse text-[14.5px] mt-4">
                  <thead>
                    <tr>
                      <th className="text-left px-4 py-2.5 pb-3.5 text-[10.5px] uppercase text-text-dim font-medium border-b border-text w-[130px]">
                        Date
                      </th>
                      <th className="text-left px-4 py-2.5 pb-3.5 text-[10.5px] uppercase text-text-dim font-medium border-b border-text">
                        Session
                      </th>
                      <th className="text-left px-4 py-2.5 pb-3.5 text-[10.5px] uppercase text-text-dim font-medium border-b border-text w-28 hidden lg:table-cell">
                        Role
                      </th>
                      <th className="text-right px-4 py-2.5 pb-3.5 text-[10.5px] uppercase text-text-dim font-medium border-b border-text w-24 hidden md:table-cell">
                        Status
                      </th>
                      <th className="text-right px-4 py-2.5 pb-3.5 text-[10.5px] uppercase text-text-dim font-medium border-b border-text w-24">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {pastRooms.map((room) => (
                      <tr key={room.id} className="hover:bg-bg2 transition-colors">
                        <td className="px-4 py-[18px] border-b border-line text-text-dim tabular-nums">
                          {room.scheduledAt ? formatDate(room.scheduledAt) : '—'}
                        </td>
                        <td className="px-4 py-[18px] border-b border-line">
                          <span className="text-text">{room.name}</span>
                          <span className="block lg:hidden text-[12.5px] text-text-dim mt-1">
                            {formatPastRole(room.role)}
                          </span>
                          {room.description && (
                            <span className="block italic text-[12.5px] text-text-dim mt-1">
                              {room.description}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-[18px] border-b border-line text-text-dim hidden lg:table-cell">
                          {formatPastRole(room.role)}
                        </td>
                        <td className="px-4 py-[18px] border-b border-line text-right text-text tabular-nums hidden md:table-cell">
                          {room.cancelledAt ? (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-warn/15 text-warn text-[11.5px] font-medium">
                              Cancelled
                            </span>
                          ) : room.closedAt ? 'Ended' : 'Expired'}
                        </td>
                        <td className="px-4 py-[18px] border-b border-line text-right">
                          <Link
                            to={`/s/${room.code}`}
                            className="text-text-dim text-[13px] border-b border-line pb-px hover:text-text hover:border-text ml-3.5"
                          >
                            View
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </details>
            )}

            {/* Followers (compact) */}
            <DashboardFollowers followers={followers} />

          </div>
        </section>
      )}

      <SharedFooter />
    </div>
  );
}

function formatRelativeTime(dateStr: string) {
  const diffMs = Date.now() - new Date(dateStr).getTime();
  const diffMinutes = Math.max(0, Math.floor(diffMs / 60000));

  if (diffMinutes < 1) return 'Just now';
  if (diffMinutes < 60) return `${diffMinutes}m ago`;

  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) return `${diffHours}h ago`;

  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) return `${diffDays}d ago`;

  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
}

function profileHref(profile: FollowProfile['profile']) {
  return profile.username ? `/u/${profile.username}` : `/u/${profile.id}`;
}

function ProfileAvatar({ profile, small }: { profile: FollowProfile['profile']; small?: boolean }) {
  const photoUrl = profile.photos[0]?.url;
  const sizeClass = small ? 'h-7 w-7 text-[11px]' : 'h-10 w-10 text-[14px]';

  if (photoUrl) {
    return (
      <img
        src={photoUrl}
        alt={profile.displayName}
        className={`${sizeClass} rounded-full object-cover`}
      />
    );
  }

  return (
    <div className={`${sizeClass} rounded-full bg-panel2 border border-line flex items-center justify-center font-medium text-text-dim`}>
      {profile.displayName[0]?.toUpperCase()}
    </div>
  );
}

function DashboardFollowers({ followers }: { followers: FollowProfile[] }) {
  if (followers.length === 0) return null;
  return (
    <section>
      <div className="flex items-baseline justify-between pb-3.5 mb-4 border-b border-line">
        <h2 className="section-title">Followers</h2>
        <span className="italic text-[12.5px] text-text-faint">{followers.length}</span>
      </div>
      <div className="flex flex-wrap gap-2">
        {followers.slice(0, 12).map(({ profile }) => (
          <Link
            key={profile.id}
            to={profileHref(profile)}
            title={profile.displayName}
            className="inline-flex items-center gap-2 rounded-full border border-line bg-white pl-1.5 pr-3 py-1 hover:border-text transition-colors"
          >
            <ProfileAvatar profile={profile} small />
            <span className="text-[12.5px] text-text truncate max-w-[140px]">{profile.displayName}</span>
          </Link>
        ))}
        {followers.length > 12 && (
          <span className="self-center text-[12px] text-text-dim ml-1">
            +{followers.length - 12} more
          </span>
        )}
      </div>
    </section>
  );
}

interface EventCardProps {
  room: Room;
  isLive?: boolean;
  formatDate: (date: string) => string;
  formatTime: (date: string) => string;
}

function HostPendingModelInviteCard({ room }: { room: Room }) {
  const scheduledDate = room.scheduledAt ? new Date(room.scheduledAt) : null;
  const dateLabel = scheduledDate
    ? scheduledDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
    : 'Date not set';
  const timeLabel = scheduledDate
    ? scheduledDate.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
    : null;
  const invitedModel = room.modelInviteName || room.modelInviteEmail || 'Invited model';

  return (
    <article className="bg-white border border-line p-5 flex gap-4">
      <div
        className="w-24 h-28 bg-[#d9d4c8] bg-cover bg-center flex-shrink-0"
        style={room.imageUrl ? { backgroundImage: `url(${room.imageUrl})` } : undefined}
      />
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <p className="text-[11px] uppercase text-text-dim mb-1">
              Awaiting model response
            </p>
            <h3 className="text-[21px] leading-[1.15] text-text truncate">
              {room.name}
            </h3>
            <p className="text-[13px] text-text-dim mt-1">
              {invitedModel} · {dateLabel}{timeLabel ? ` at ${timeLabel}` : ''}
            </p>
          </div>
          <Link
            to="/create"
            state={{ roomCode: room.code, section: 'model' }}
            className="text-[12.5px] text-text-dim border-b border-line pb-px hover:text-text hover:border-text flex-shrink-0"
          >
            Manage
          </Link>
        </div>

        {room.description && (
          <p className="text-[13px] text-text-dim leading-[1.45] mt-3 line-clamp-2">
            {room.description}
          </p>
        )}

        <div className="flex flex-wrap gap-2 mt-4">
          <span className="px-2.5 py-1 rounded-full bg-amber-100 text-[11.5px] text-amber-700">
            Invitation pending
          </span>
          <span className="px-2.5 py-1 rounded-full bg-bg text-[11.5px] text-text-dim">
            Not listed as upcoming yet
          </span>
        </div>
      </div>
    </article>
  );
}

function ModelInvitationCard({
  invitation,
  responding,
  onAccept,
  onDecline,
}: {
  invitation: ModelInvitation;
  responding: boolean;
  onAccept: () => void;
  onDecline: () => void;
}) {
  const { room } = invitation;
  const scheduledDate = room.scheduledAt ? new Date(room.scheduledAt) : null;
  const dateLabel = scheduledDate
    ? scheduledDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
    : 'Date not set';
  const timeLabel = scheduledDate
    ? scheduledDate.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
    : null;
  const guaranteeLabel = `$${((room.modelGuarantee ?? 0) / 100).toFixed(0)} guarantee`;
  const ticketLabel = room.pricingType === 'fixed' && room.ticketPrice
    ? `$${(room.ticketPrice / 100).toFixed(2)} tickets`
    : room.pricingType === 'donation'
    ? `Donation · suggested $${((room.suggestedMinimum ?? 0) / 100).toFixed(2)}`
    : 'Free';

  return (
    <article className="bg-white border border-line p-5 flex gap-4">
      <div
        className="w-24 h-28 bg-[#d9d4c8] bg-cover bg-center flex-shrink-0"
        style={room.imageUrl ? { backgroundImage: `url(${room.imageUrl})` } : undefined}
      />
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <p className="text-[11px] uppercase text-text-dim mb-1">
              Model invitation
            </p>
            <h3 className="text-[21px] leading-[1.15] text-text truncate">
              {room.name}
            </h3>
            <p className="text-[13px] text-text-dim mt-1">
              {invitation.hostName ? `Hosted by ${invitation.hostName}` : 'Hosted session'} · {dateLabel}{timeLabel ? ` at ${timeLabel}` : ''}
            </p>
          </div>
        </div>

        {room.description && (
          <p className="text-[13px] text-text-dim leading-[1.45] mt-3 line-clamp-2">
            {room.description}
          </p>
        )}

        <div className="flex flex-wrap gap-2 mt-4">
          <span className="px-2.5 py-1 rounded-full bg-bg text-[11.5px] text-text-dim">
            {guaranteeLabel}
          </span>
          <span className="px-2.5 py-1 rounded-full bg-bg text-[11.5px] text-text-dim">
            {room.revenueSplitMode === 'even' ? 'Even split' : `${room.modelSplitPercent}% model split`}
          </span>
          <span className="px-2.5 py-1 rounded-full bg-bg text-[11.5px] text-text-dim">
            {ticketLabel}
          </span>
        </div>

        <div className="flex items-center gap-2.5 mt-5">
          <Link
            to={`/s/${room.code}`}
            className="inline-flex items-center justify-center h-9 px-4 rounded border border-line text-[13px] font-medium text-text-dim hover:text-text hover:border-text transition-colors"
          >
            Details
          </Link>
          <button
            onClick={onAccept}
            disabled={responding}
            className="inline-flex items-center justify-center h-9 px-4 rounded bg-text text-bg text-[13px] font-medium hover:bg-black transition-colors disabled:opacity-50"
          >
            {responding ? 'Saving...' : 'Accept'}
          </button>
          <button
            onClick={onDecline}
            disabled={responding}
            className="inline-flex items-center justify-center h-9 px-4 rounded border border-line text-[13px] font-medium text-text-dim hover:text-text hover:border-text transition-colors disabled:opacity-50"
          >
            Decline
          </button>
        </div>
      </div>
    </article>
  );
}

function EventCard({ room, isLive, formatDate, formatTime }: EventCardProps) {
  // Calculate duration in minutes
  const getDuration = () => {
    if (!room.scheduledAt || !room.endsAt) return null;
    const start = new Date(room.scheduledAt);
    const end = new Date(room.endsAt);
    const minutes = Math.round((end.getTime() - start.getTime()) / 60000);
    if (minutes >= 60) {
      const hours = Math.floor(minutes / 60);
      const mins = minutes % 60;
      return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
    }
    return `${minutes}m`;
  };

  const duration = getDuration();
  const isParticipantSession = room.role === 'modeling' || room.role === 'attending';
  const destination = isParticipantSession || room.isPublished ? `/s/${room.code}` : '/create';
  const destinationState = isParticipantSession || room.isPublished
    ? undefined
    : { roomCode: room.code, section: 'details' };
  const roleLabel = room.role === 'modeling'
    ? 'Modeling'
    : room.role === 'attending'
    ? 'Attending'
    : 'Hosting';

  const getStatusText = () => {
    if (room.cancelledAt) return 'Cancelled';
    if (isLive) return 'Live now';
    if (room.role === 'attending') return 'Ticket held';
    if (room.seatsTotal) {
      return `${room.seatsConfirmed}/${room.seatsTotal} sales`;
    }
    return `${room.seatsConfirmed} sales`;
  };

  // Short date/time format for pill
  const getShortDateTime = () => {
    if (!room.scheduledAt) return null;
    const date = new Date(room.scheduledAt);
    const weekday = date.toLocaleDateString('en-US', { weekday: 'short' });
    const month = date.toLocaleDateString('en-US', { month: 'short' });
    const day = date.getDate();
    const hour = date.getHours();
    const minute = date.getMinutes();
    const ampm = hour >= 12 ? 'pm' : 'am';
    const hour12 = hour % 12 || 12;
    const timeStr = minute > 0 ? `${hour12}:${minute.toString().padStart(2, '0')}${ampm}` : `${hour12}${ampm}`;
    return `${weekday} ${month} ${day} · ${timeStr}`;
  };

  return (
    <Link
      to={destination}
      state={destinationState}
      className="bg-white border border-line flex flex-col overflow-hidden transition-all hover:border-text hover:-translate-y-0.5"
    >
      {/* Card Art */}
      <div
        className="relative w-full aspect-[4/5] bg-[#d9d4c8] bg-cover bg-center"
        style={room.imageUrl ? { backgroundImage: `url(${room.imageUrl})` } : undefined}
      >
        {/* Pill - seat counts */}
        <span
          className="absolute top-3.5 left-3.5 inline-flex items-center gap-2 px-2.5 py-1.5 bg-white/95 rounded-full text-[10.5px] uppercase text-text"
        >
          <span
            className={`w-1.5 h-1.5 rounded-full ${
              isLive ? 'bg-[#c11] shadow-[0_0_0_3px_rgba(204,17,17,0.18)] animate-pulse' : 'bg-text'
            }`}
          />
          {getStatusText()}
        </span>

        {/* Date & Time pill */}
        {room.scheduledAt && (
          <span className="absolute top-3.5 right-3.5 px-2.5 py-1.5 bg-white/95 rounded-full text-[10.5px] uppercase text-text">
            {getShortDateTime()}
          </span>
        )}

        {/* Overlay title */}
        <span className="absolute left-4 right-4 bottom-4 text-[22px] font-normal leading-[1.12] text-white drop-shadow-[0_2px_16px_rgba(0,0,0,0.6)]">
          {room.name}
        </span>
      </div>

      {/* Card Body */}
      {(room.description || room.modelName) && (
        <div className="px-[18px] py-3.5 flex flex-col gap-1.5">
          {room.description && (
            <p className="text-[13px] text-text-dim line-clamp-2 leading-[1.45]">
              {room.description}
            </p>
          )}
          {room.modelName && (
            <span className="text-[13px] text-text">
              with <span className="font-medium">{room.modelName}</span>
            </span>
          )}
        </div>
      )}

      {/* Card Footer */}
      <div className="flex items-center gap-2 px-[18px] py-3.5 border-t border-line-soft bg-bg">
        <span className="flex-1 italic text-[12.5px] text-text-dim">
          {roleLabel} · {room.isPublic ? 'Public' : 'Private'} · {room.isTutored ? 'Tutored' : 'No guest limit'}
        </span>
        <span className="text-[12.5px] text-text border-b border-line pb-px hover:border-text">
          {isParticipantSession ? 'View' : 'Manage'}
        </span>
      </div>
    </Link>
  );
}

interface DraftRowProps {
  room: Room;
  formatDate: (date: string) => string;
  formatTime: (date: string) => string;
  onContinue: () => void;
}

function DraftRow({ room, formatDate, formatTime, onContinue }: DraftRowProps) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-[minmax(0,1.6fr)_1fr_auto] items-center gap-6 py-[22px] border-b border-line hover:bg-bg2 transition-colors">
      <div>
        <p className="text-[19px] font-normal text-text mb-1">{room.name}</p>
        {room.description && (
          <p className="italic text-[13px] text-text-dim leading-[1.5] line-clamp-2">
            {room.description}
          </p>
        )}
      </div>
      <div className="text-[13.5px] text-text">
        {room.scheduledAt ? (
          <div className="flex flex-col gap-0.5">
            <span>{formatDate(room.scheduledAt)}</span>
            <span className="text-[12.5px] text-text-dim">{formatTime(room.scheduledAt)}</span>
          </div>
        ) : (
          <span className="italic text-text-faint">No date set</span>
        )}
      </div>
      <div className="flex gap-2.5">
        <button
          onClick={(e) => {
            e.preventDefault();
            // TODO: Add discard functionality
          }}
          className="inline-flex items-center justify-center h-8 px-3 rounded border border-line text-[12.5px] font-medium text-text-dim hover:text-text hover:border-text transition-colors"
        >
          Discard
        </button>
        <button
          onClick={onContinue}
          className="inline-flex items-center justify-center h-8 px-3 rounded bg-text text-bg text-[12.5px] font-medium hover:bg-black transition-colors"
        >
          Continue
        </button>
      </div>
    </div>
  );
}
